/** Thin fetch wrapper: adds the Cognito ID token (refreshed once on a 401), parses JSON, turns HTTP errors into ApiError. */
import { config } from '@/lib/config'
import { getAuthToken } from '@/auth/AuthContext'

export class ApiError extends Error {
  status: number
  blockers: string[]
  payload: unknown
  constructor(status: number, message: string, payload: unknown, blockers: string[] = []) {
    super(message)
    this.status = status
    this.payload = payload
    this.blockers = blockers
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  signal?: AbortSignal
  /** Disable for read-only queries transported via POST. */
  notifyBoardChange?: boolean
  body?: unknown
  query?: Record<string, string | number | undefined>
  /** Return the parsed payload for these non-2xx statuses instead of throwing (e.g. 422 on file complete). */
  acceptStatuses?: number[]
}

export async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const url = new URL(config.apiUrl + path)
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
  const send = (token: string | null) => fetch(url.toString(), {
    method: opts.method ?? 'GET',
    signal: opts.signal,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })
  const token = await getAuthToken()
  let res = await send(token)
  // The browser judges token expiry by its own clock, so a wrong or changed laptop clock can
  // keep sending a token Cognito already rejects. A 401 never reaches the handler, so asking
  // Cognito for a fresh token and sending once more is safe for any method. A dead session
  // refreshes to no token and keeps the 401; a Cognito outage throws, so it is not mistaken
  // for a sign-in problem.
  if (res.status === 401 && token) {
    const fresh = await getAuthToken({ forceRefresh: true })
    if (fresh) res = await send(fresh)
  }
  const text = await res.text()
  let payload: unknown = null
  try { payload = text ? JSON.parse(text) : null } catch { payload = text }
  if (!res.ok && !(opts.acceptStatuses ?? []).includes(res.status)) {
    if (res.status === 403 && (path === '/sme/reviews' || /\/reviews\/|\/conversations\/peer-/.test(path)))
      window.dispatchEvent(new Event('review-access-check'))
    const p = (payload ?? {}) as { message?: string; blockers?: string[] }
    throw new ApiError(res.status, p.message ?? `Request failed (${res.status})`, payload, p.blockers ?? [])
  }
  if (opts.method && opts.method !== 'GET' && opts.notifyBoardChange !== false) window.dispatchEvent(new Event('board-data-changed'))
  return payload as T
}

export const api = {
  get: <T>(path: string, query?: Options['query']) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown, extra?: Partial<Options>) => request<T>(path, { method: 'POST', body, ...extra }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
